import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import roleService from "../../../../service/roleService";
import permissionService from "../../../../service/permissionService";
import { Permission } from "../types/permissions";
import AddRoleLayout from "./AddRoleLayout";
import RoleBasicsStep from "./steps/RoleBasicsStep";
import RolePermissionsStep from "./steps/RolePermissionsStep";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";

const STEPS = ["Basics", "Permissions"];

const AddRolePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState("");
  const [granted, setGranted] = useState<Set<number>>(new Set());
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      setLoadingPermissions(true);
      try {
        const permissions = await permissionService.getAllPermissions();
        setAllPermissions(permissions);
      } catch (err) {
        console.error("Failed to load permissions", err);
        showErrorToast("Failed to load permissions");
      } finally {
        setLoadingPermissions(false);
      }
    };
    loadPermissions();
  }, []);

  const handleBack = () => navigate("/configurations/manage-roles");

  const handleNext = () => {
    if (activeStep === 0 && !name.trim()) {
      showErrorToast("Role name is required");
      return;
    }
    if (activeStep < STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    } else {
      handleBack();
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showErrorToast("Role name is required");
      setActiveStep(0);
      return;
    }

    setIsSubmitting(true);
    try {
      const newRole = await roleService.createRole({ name: name.trim() });
      if (granted.size > 0) {
        await roleService.updateRole(newRole.id, {
          permissions: Array.from(granted),
        });
      }
      showSuccessToast(`Role "${newRole.name}" created successfully`);
      navigate("/configurations/manage-roles");
    } catch (err) {
      console.error("Failed to create role", err);
      showErrorToast("Failed to create role");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingPermissions) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AddRoleLayout
      steps={STEPS}
      activeStep={activeStep}
      roleName={name}
      selectedCount={granted.size}
      totalCount={allPermissions.length}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSubmit={handleSubmit}
      onStepClick={(step) => {
        if (step < activeStep) setActiveStep(step);
      }}
      isSubmitting={isSubmitting}
    >
      {activeStep === 0 ? (
        <RoleBasicsStep name={name} onNameChange={setName} />
      ) : (
        <RolePermissionsStep
          allPermissions={allPermissions}
          granted={granted}
          setGranted={setGranted}
        />
      )}
    </AddRoleLayout>
  );
};

export default AddRolePage;
